/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import React, { Fragment } from 'react';
import { Typography } from '../../../atoms';
import { Pricify } from '../../../lib/price';
import {
	ICanUseOtherPaymentMethod,
	IPayingUser,
	IPaymentMethod,
	PaymentMethodPicker,
} from '../../../molecules/PaymentMethodPicker';
import { ETypographyColor, ETypography } from '../../../types';
import { useRefundPopup } from '../hooks/useRefundPopup';
import { flexColCss, hrCss, justifyCenterCss, titleCss } from '../style';
import { ThemeColors as colors } from '../../../styles/theme';
import { ActionSelect, ArchiveSlotInvoiceActionEnum } from '../../../molecules/ActionSelect';

const containerCss = css`
	margin-bottom: 20px;
	height: 100%;
	overflow-x: clip;
`;

/**
 * Fees-only and reservation-refund flows only offer leave-balance; otherwise the user can also remove the item.
 */
export function getRefundPopupActions(
	isFeesOnlyRefund: boolean,
	isReservationRefund: boolean
): ArchiveSlotInvoiceActionEnum[] {
	if (isFeesOnlyRefund || isReservationRefund) {
		return [ArchiveSlotInvoiceActionEnum.REFUND];
	}

	return [ArchiveSlotInvoiceActionEnum.REFUND, ArchiveSlotInvoiceActionEnum.REFUND_AND_VOID];
}

interface Props extends ICanUseOtherPaymentMethod {
	amountToRefund: number;
	usedPaymentMethods: IPaymentMethod[];
	handleSelectedMethods: (val: IPaymentMethod[]) => void;
	handleSelectAction: (val: ArchiveSlotInvoiceActionEnum) => void;
	handleDisable?: (val: boolean) => void;
	disableActions?: boolean;
	isReservationRefund?: boolean;
	/** When true, only "Refund and Leave Balance Due" is shown (leave-balance submit). */
	isFeesOnlyRefund?: boolean;
	isPublicInvoice?: boolean;
	invoiceOwner?: IPayingUser;
	replenishStockOption?: React.ReactNode;
	showReplenishStockOption?: boolean;
}

export const PaymentMethodStep = ({
	amountToRefund,
	usedPaymentMethods,
	handleSelectedMethods,
	handleSelectAction,
	handleDisable,
	disableActions = false,
	isReservationRefund = false,
	isFeesOnlyRefund = false,
	isPublicInvoice = false,
	invoiceOwner,
	canUseOtherPaymentMethod,
	replenishStockOption,
	showReplenishStockOption = false,
}: Props) => {
	const { labels } = useRefundPopup();

	const stepLabels = labels[0];

	const onChangeOfDetails = (paymentMethods: IPaymentMethod[]) => {
		const selected = paymentMethods.filter(pm => pm.selected);
		handleSelectedMethods(selected);
	};

	const actions = getRefundPopupActions(isFeesOnlyRefund, isReservationRefund);

	return (
		<div css={containerCss} data-aid="PaymentMethodStep-steps">
			<div css={[flexColCss, titleCss]}>
				<Typography css={justifyCenterCss} color={ETypographyColor.primary} type={ETypography.body1Accented}>
					{stepLabels.refundAmount} {Pricify(amountToRefund)}
				</Typography>
				<Typography color={ETypographyColor.secondary} type={ETypography.body2}>
					{stepLabels.refundAmountSubtitle}
				</Typography>
			</div>
			<PaymentMethodPicker
				showAmount
				onChangeOfDetails={onChangeOfDetails}
				handleDisable={handleDisable}
				totalPrice={amountToRefund}
				usedPaymentMethods={usedPaymentMethods}
				invoiceOwner={invoiceOwner}
				isPublicInvoice={isPublicInvoice}
				canUseOtherPaymentMethod={canUseOtherPaymentMethod}
			/>
			{!disableActions && (
				<Fragment>
					<hr css={hrCss(colors)} />
					<ActionSelect
						actions={actions}
						onActionSelect={handleSelectAction}
					/>
				</Fragment>
			)}
			{showReplenishStockOption && replenishStockOption}
		</div>
	);
};
